import React, { useState, useEffect, useCallback } from 'react';
import { Center } from 'native-base';
import {
    Reward,
    useRedeemRewards,
    useSpringBigStore,
    useI18n,
    getMember,
    RedeemResponse,
    updateMember,
    useAppSettings
} from '@springbig/shared';
import _ from 'lodash';
import Alert from '~/shared/Alert';
import { DefaultValuesPointsProps } from '~/shared/RangeBar';
import RedeemedRewardModal from '~/shared/RedeemedReward';
import RewardSliderBox, { RewardSliderBoxSkeleton } from './components/RewardSliderBox';
import RewardHeader, { RewardHeaderSkeleton } from './components/RewardHeader';
import { useToast } from '~/hooks/toast';
import { useAuthorization } from '~/hooks/auth';
import Loading from '~/shared/Loading';
import ExtensionDetailsModal from '~/shared/ExtensionDetailsModal';
import SuspendedMerchantScreen from '~/shared/SuspendedMerchantScreen';
import { NoDataPlaceholder } from '~/shared/NoDataPlaceholder';
import { faAward } from '@fortawesome/free-solid-svg-icons';
import { useAppNavigate } from '~/pages';
import { Section } from '~/shared/Theme';
import RewardsSkeleton from './components/RewardsSkeleton';

export type BuilderRewards = {
    [key: string]: Reward[]
};

export type ShowAlertState = {
    status: boolean,
    id: number | null,
    title?: string
};

const RewardsScreen = () => {

    const { params } = useAppNavigate();
    const { redeemRewardInStore } = useAppSettings();
    const { id } = params || {};

    const [showAlert, setShowAlert] =
        useState<ShowAlertState>({ status: false, id: null, title: undefined });

    const [successRedeem, setSuccessRedeem] =
        useState<RedeemResponse | null>(null);

    const [rewards, setRewards] = useState<BuilderRewards>({});
    const [rewardCheckPoints, setRewardCheckPoints] = useState<DefaultValuesPointsProps[]>([]);
    const [rewardMaxAmount, setRewardMaxAmount] = useState<number>();
    const [showLoyaltyAlert, setShowLoyaltyAlert] = useState<boolean>(false);
    const [loyaltySign, setLoyaltySign] = useState<string>();

    const { errorToast } = useToast();
    const { t } = useI18n();
    const { member, walletId, setMember } = useSpringBigStore();
    const { isLoadingAuth } = useAuthorization();
    const [rewardsLoading, setRewardsLoading] = useState(true);

    const {
        mutate: redeemReward,
        data: redeemData,
        isLoading: redeemIsLoading
    } = useRedeemRewards();

    const syncMember = useCallback(
        async (reward?: RedeemResponse) => {

            const updatedMember = walletId && await getMember({ walletId });

            if (!updatedMember) {
                return;
            }

            setMember(updatedMember);

            if (reward) {
                setSuccessRedeem(reward);
            }
        },
        [setMember, setSuccessRedeem, walletId]
    );

    const handleExpandRewardDetails = useCallback((id: number, title?: string) => {

        setShowAlert({
            status: true,
            id,
            title
        });
    }, [setShowAlert]);

    const handleAcceptRedeem = useCallback(() => {

        setShowAlert(prevState => ({
            ...prevState,
            status: false
        }));

        if (!member) {
            return;
        }

        const reward = member.rewards.find(el => el.id === showAlert.id);

        if (!reward) {
            return;
        }

        if (reward.item_value! > member.balance!) {
            errorToast('You don\'t have enough balance to redeem this reward');
            return;
        }

        redeemReward({ reward_id: showAlert.id!, type: 'reward' });
    }, [member, errorToast, redeemReward, showAlert]);

    const getRewards = () => {

        if (!member) {
            return;
        }

        if (!member.rewards) {
            return;
        }

        setRewardsLoading(true);

        const _rewards: BuilderRewards = {};
        const _rewardCheckPoints: DefaultValuesPointsProps[] = [];
        const indexValues = [
            ...new Set(
                member.rewards.map(
                    r => r.item_value! * 1
                )
            )
        ].sort(
            (a, b) => a - b
        );

        indexValues.forEach(key => {

            _rewards[`reward-${key}`] = member.rewards.filter(

                (reward) => reward.item_value === key
            );

            if (key) {
                _rewardCheckPoints.push({
                    value: key,
                    label: `${key}`,
                    divisbleValue: 1
                });
            }
        });

        const maxVal = Math.max(..._rewardCheckPoints.map(el => el.value));

        for (const cp of _rewardCheckPoints) {

            cp.divisbleValue = (cp.value * 100 / maxVal) / 100;
        }

        const _values: DefaultValuesPointsProps[] = [];
        const _balance = member.balance || 0;

        const _next = _rewardCheckPoints && _.orderBy(_rewardCheckPoints, 'value', 'asc').find((point) => point.value > _balance);
        const nextValue = _next?.value || maxVal || 0;

        _values.push({
            value: nextValue,
            label: `${nextValue}`,
            divisbleValue: 1
        });

        const _first = _rewardCheckPoints && _.orderBy(_rewardCheckPoints, 'value', 'desc').find((point) => point.value < nextValue);
        const firstValue = _first?.value || 0;

        _values.push({
            value: firstValue,
            label: `${firstValue}`,
            divisbleValue: 0.0001
        });

        setRewards(_rewards);
        setRewardsLoading(false);
        setRewardCheckPoints(_values);
        setRewardMaxAmount(nextValue);

        if (!member.allowed_loyalty) {
            setShowLoyaltyAlert(true);
        }

        setLoyaltySign(undefined);
    };

    useEffect(() => {

        getRewards();
    }, [
        member,
        setRewards,
        setRewardCheckPoints,
        setRewardMaxAmount
    ]);

    const handleLoyaltySign = async () => {

        setShowLoyaltyAlert(false);

        if (!loyaltySign) {
            return;
        }

        await updateMember({
            payload: {
                member: {
                    merchant_id: member?.merchant_id,
                    phone_number: member?.phone_number,
                    allowed_loyalty: true,
                },
                signature: loyaltySign
            },
            walletId: walletId!,

        });

        await syncMember();
    };

    useEffect(() => {

        if (!redeemData || !walletId) {
            return;
        }

        syncMember(redeemData);
    }, [redeemData, walletId, syncMember]);

    if (isLoadingAuth) {
        return <Loading isLoading={isLoadingAuth} />;
    }

    const handleModalClose = (): void => {

        setShowAlert({
            status: false,
            id: null,
            title: undefined
        });
    };

    useEffect(() => {

        if (id) {
            const int = Number(id);

            if (!isNaN(int)) {
                setTimeout(() => {

                    setShowAlert({ status: true, id: int, title: null });
                }, 1000);
            }
        }
    }, [id]);

    const [loading, setIsLoading] = useState(true);

    useEffect(() => {

        setIsLoading(redeemIsLoading && !rewards);
    }, [redeemIsLoading, rewards]);

    if (loading) {
        return <RewardsSkeleton />;
    }

    if ((!rewards || !Object.entries(rewards).length) && !rewardsLoading) {
        return (
            <Section>
                <NoDataPlaceholder
                    title={'common.rewards_placeholder_title'}
                    message={'common.rewards_placeholder_subtitle'}
                    callback={getRewards}
                    isLoading={rewardsLoading}
                    iconName={faAward}
                />
            </Section>
        );
    }

    return (
        <Section>
            { showAlert.id &&
                <ExtensionDetailsModal
                    showModal={showAlert.status}
                    extensionId={showAlert.id}
                    type={'rewards'}
                    onCloseModal={handleModalClose}
                    onRedeem={handleAcceptRedeem}
                    isInStore={redeemRewardInStore}
                />
            }
            { rewardsLoading ?
                <RewardHeaderSkeleton />
                :
                <RewardHeader
                    member={member}
                    rewardCheckPoints={rewardCheckPoints}
                    rewardMaxAmount={rewardMaxAmount}
                    setShowLoyaltyAlert={setShowLoyaltyAlert}
                />
            }
            <Center alignItems='center'>
                <RewardSliderBox
                    handleAcceptRedeemReward={handleExpandRewardDetails}
                    redeemIsLoading={redeemIsLoading}
                    rewards={rewards}
                />
            </Center>
            { successRedeem &&
                <RedeemedRewardModal
                    memberPhoneNumber={member?.phone_number || ''}
                    createdAt={successRedeem.created_at}
                    confirmationNumber={successRedeem.id}
                    returnWallet={() => setSuccessRedeem(null)}
                    isOpen={true}
                    image={successRedeem.reward.images}
                    description={successRedeem.reward.description || ''}
                    title={successRedeem.reward.item_name}
                />
            }
            { showLoyaltyAlert &&
                <Alert
                    title={t('alerts.loyalty_opt_in')}
                    message={t('alerts.loyalty_opt_in_message')}
                    signature={true}
                    setSignature={setLoyaltySign}
                    setAcceptAlert={() => handleLoyaltySign()}
                    setCancelAlert={() => setShowLoyaltyAlert(false)}
                    showAlert={showLoyaltyAlert}
                    textConfirmButton='Opt in'
                />
            }
            { member && member.show_pending_modal &&
                <SuspendedMerchantScreen />
            }
        </Section>
    );
};

export default RewardsScreen;
